/**
 * Единственное место, где приложение ходит в сеть.
 * Компоненты не вызывают fetch напрямую — только эти функции.
 */

import {
  API_ROUTES,
  type ApiError,
  type CarsRequest,
  type CarsResponse,
  type ConfigResponse,
  type GasStation,
  type LoginConfirmRequest,
  type LoginStartRequest,
  type LoginStartResult,
  type MeResponse,
  type Plan,
  type PlannerSettings,
  type PlanRequest,
} from '@cdf/shared';

/** Ошибка запроса с кодом и разобранным телом ApiError, если оно пришло. */
export class ApiRequestError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.code = code;
    this.details = details;
  }

  /** Сеть недоступна — отдельный случай: предлагаем «Повторить». */
  get isNetwork(): boolean {
    return this.status === 0;
  }

  get isUnauthorized(): boolean {
    return this.status === 401 || this.status === 403;
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | undefined>;
  signal?: AbortSignal;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined) params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

function isApiError(value: unknown): value is ApiError {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as ApiError).error === 'string'
  );
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query, signal } = options;

  let response: Response;
  try {
    response = await fetch(buildUrl(path, query), {
      method,
      signal,
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw new ApiRequestError(
      0,
      'network',
      'Нет связи с сервером. Проверьте интернет.',
      cause,
    );
  }

  const raw = await response.text();
  let parsed: unknown = null;
  if (raw.length > 0) {
    try {
      parsed = JSON.parse(raw);
    } catch {
      parsed = null;
    }
  }

  if (!response.ok) {
    if (isApiError(parsed)) {
      throw new ApiRequestError(
        response.status,
        parsed.error,
        parsed.message || 'Запрос не выполнен',
        parsed.details,
      );
    }
    throw new ApiRequestError(
      response.status,
      'http_error',
      `Сервер ответил ${response.status}`,
      raw.slice(0, 200),
    );
  }

  return parsed as T;
}

/** Подставляет :id / :index в шаблон пути из API_ROUTES. */
function fillPath(template: string, params: Record<string, string | number>): string {
  return template.replace(/:([a-zA-Z]+)/g, (match, key: string) => {
    const value = params[key];
    return value === undefined ? match : encodeURIComponent(String(value));
  });
}

/** Состояние шага на сервере. Поле progress не описано в контрактах — читаем мягко. */
export type StopProgress = 'done' | 'skipped' | 'pending';

export interface PlanBundle {
  plan: Plan;
  stations: GasStation[];
  mock: boolean;
  /** Индекс шага → его состояние. Пусто, если сервер прогресс не отдал. */
  progress: Record<number, StopProgress>;
}

function extractPlan(payload: unknown): Plan | null {
  if (typeof payload !== 'object' || payload === null) return null;
  const record = payload as Record<string, unknown>;
  const candidate = 'plan' in record ? record['plan'] : record;
  if (
    typeof candidate === 'object' &&
    candidate !== null &&
    Array.isArray((candidate as Plan).stops)
  ) {
    return candidate as Plan;
  }
  return null;
}

function extractProgress(payload: unknown): Record<number, StopProgress> {
  const out: Record<number, StopProgress> = {};
  if (typeof payload !== 'object' || payload === null) return out;
  const raw = (payload as Record<string, unknown>)['progress'];
  if (typeof raw !== 'object' || raw === null) return out;

  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    const index = Number(key);
    if (!Number.isInteger(index) || index < 0) continue;
    if (value === 'done' || value === 'skipped' || value === 'pending') {
      out[index] = value;
    }
  }
  return out;
}

function extractStations(payload: unknown): GasStation[] {
  if (typeof payload !== 'object' || payload === null) return [];
  const raw = (payload as Record<string, unknown>)['stations'];
  return Array.isArray(raw) ? (raw as GasStation[]) : [];
}

/** Приводит любой ответ с планом к общему виду. null — плана нет. */
function toBundle(payload: unknown): PlanBundle | null {
  const plan = extractPlan(payload);
  if (!plan) return null;
  const mock =
    typeof payload === 'object' &&
    payload !== null &&
    (payload as Record<string, unknown>)['mock'] === true;
  return {
    plan,
    stations: extractStations(payload),
    mock,
    progress: extractProgress(payload),
  };
}

/** Ответ /api/settings: серверные настройки сессии и дефолты сервиса. */
export interface SettingsPayload {
  settings: PlannerSettings | null;
  defaults: PlannerSettings | null;
}

function readSettings(payload: unknown): SettingsPayload {
  if (typeof payload !== 'object' || payload === null) {
    return { settings: null, defaults: null };
  }
  const record = payload as Record<string, unknown>;
  const pick = (value: unknown): PlannerSettings | null =>
    typeof value === 'object' &&
    value !== null &&
    typeof (value as PlannerSettings).targetBonus === 'number'
      ? (value as PlannerSettings)
      : null;
  return { settings: pick(record['settings']), defaults: pick(record['defaults']) };
}

export const api = {
  getConfig: (signal?: AbortSignal): Promise<ConfigResponse> =>
    request<ConfigResponse>(API_ROUTES.config, { signal }),

  getMe: (signal?: AbortSignal): Promise<MeResponse> =>
    request<MeResponse>(API_ROUTES.me, { signal }),

  loginStart: (phone: string, signal?: AbortSignal): Promise<LoginStartResult> =>
    request<LoginStartResult>(API_ROUTES.loginStart, {
      method: 'POST',
      body: { phone } satisfies LoginStartRequest,
      signal,
    }),

  /**
   * Подтверждение кода. Тело ответа не описано в контрактах, поэтому
   * не полагаемся на него: после успеха вызывающий код перезапрашивает /me.
   */
  loginConfirm: async (
    payload: LoginConfirmRequest,
    signal?: AbortSignal,
  ): Promise<void> => {
    await request<unknown>(API_ROUTES.loginConfirm, {
      method: 'POST',
      body: payload,
      signal,
    });
  },

  logout: async (): Promise<void> => {
    await request<unknown>(API_ROUTES.logout, { method: 'POST' });
  },

  getCars: (params: CarsRequest, signal?: AbortSignal): Promise<CarsResponse> =>
    request<CarsResponse>(API_ROUTES.cars, {
      query: {
        lat: params.lat,
        lon: params.lon,
        radiusKm: params.radiusKm,
      },
      signal,
    }),

  createPlan: async (
    payload: PlanRequest,
    signal?: AbortSignal,
  ): Promise<PlanBundle> => {
    const bundle = toBundle(
      await request<unknown>(API_ROUTES.plan, {
        method: 'POST',
        body: payload,
        signal,
      }),
    );
    if (!bundle) {
      throw new ApiRequestError(0, 'bad_plan', 'Сервер вернул план в неизвестном виде');
    }
    return bundle;
  },

  /**
   * Активный план, если сервер его помнит.
   * Пустое тело (204) и 404 одинаково означают «плана нет».
   */
  getActivePlan: async (signal?: AbortSignal): Promise<PlanBundle | null> => {
    try {
      return toBundle(await request<unknown>(API_ROUTES.planActive, { signal }));
    } catch (error) {
      if (error instanceof ApiRequestError && error.status === 404) return null;
      throw error;
    }
  },

  completeStop: async (planId: string, index: number): Promise<PlanBundle | null> =>
    toBundle(
      await request<unknown>(fillPath(API_ROUTES.planComplete, { id: planId, index }), {
        method: 'POST',
      }),
    ),

  skipStop: async (planId: string, index: number): Promise<PlanBundle | null> =>
    toBundle(
      await request<unknown>(fillPath(API_ROUTES.planSkip, { id: planId, index }), {
        method: 'POST',
      }),
    ),

  getSettings: async (signal?: AbortSignal): Promise<SettingsPayload> =>
    readSettings(await request<unknown>(API_ROUTES.settings, { signal })),

  /** Частичное обновление: сервер сам смёржит с текущими. */
  putSettings: async (patch: Partial<PlannerSettings>): Promise<SettingsPayload> =>
    readSettings(
      await request<unknown>(API_ROUTES.settings, { method: 'PUT', body: patch }),
    ),
};

export const EVENTS_URL = API_ROUTES.events;
